import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { MetaService } from "@ngx-meta/core";
import { environment } from "../../../environments/environment";
import {
  DetailedProduct,
  DetailedSolution
} from "../../core/models/products.model";
import { SiilaTranslationService } from "../../core/services/siila-translation.service";
import { NewsArticleAuthor, NewsDetail } from "../models/news.model";
import { clearPrerenderRedirect } from "../utils/prerender";
import { PrerenderReadyService } from "./prerender-ready.service";

export interface MetaTag {
  name: string;
  content: string;
}

@Injectable()
export class MetaTagService {
  RESOURCE_SOCIAL_SHARE_IMG =
    "/assets/icons/RESOURCE-SOCIAL-SHARE-SiiLA-May9-HR.png";

  /**
   * URL prefixes — language suffix already stripped, query/fragment removed,
   * lowercased — whose pages must never be indexed. REsource tag/topic listings
   * and the news keyword search are pagination shells, not destination pages.
   *
   * `/resource` (the hub) and the article routes are deliberately absent: they
   * share this app shell and must stay indexable. Adding a prefix here is the
   * ONLY supported way to make a route noindex at runtime.
   */
  private readonly NOINDEX_URL_PREFIXES: string[] = [
    "/resource/tag",
    "/resource/tags",
    "/resource/topic",
    "/search_news"
  ];

  /**
   * Hosts that are never indexable, whatever the route. Moved here verbatim
   * from the old `AppComponent.validateNoIndexNoFollowTag()` switch so a single
   * method decides the robots tag — otherwise a route-scoped "remove" would
   * strip staging's site-wide noindex on the first in-app navigation.
   */
  private readonly NOINDEX_HOSTS: string[] = [
    "stg.siila.com.br",
    "www.stg.siila.com.br",
    "test.siila.com.br",
    "www.test.siila.com.br",
    "dev.siila.com.br",
    "www.dev.siila.com.br",
    "stg.siila.com.mx",
    "www.stg.siila.com.mx",
    "test.siila.com.mx",
    "www.test.siila.com.mx",
    "dev.siila.com.mx",
    "www.dev.siila.com.mx",
    "stg.siila.com",
    "www.stg.siila.com",
    "localhost",
    "localhost:4200"
  ];

  private readonly NEWS_ARTICLE_JSONLD_ID = "news-article-jsonld";
  private readonly SITE_BRAND = "SiiLA";
  private readonly NEWS_IMAGE_WIDTH = 800;
  private readonly NEWS_IMAGE_HEIGHT = 600;

  /** Maps the app's internal language code to its hreflang attribute value. */
  private readonly HREFLANG_CODE: { [lang: string]: string } = {
    en: "en",
    pt: "pt-BR",
    es: "es"
  };

  constructor(
    private title: Title,
    private metaSrv: MetaService,
    private siilaTranslationService: SiilaTranslationService,
    private prerenderReadyService: PrerenderReadyService
  ) {}

  private setDynamicTags(
    tags: MetaTag[],
    title?: string,
    noUpperCase?: boolean
  ) {
    if (title && (!noUpperCase || noUpperCase == undefined)) {
      // SIIL-6159
      //title = title.toUpperCase();
    }
    this.title.setTitle(title || this.title.getTitle());
    if (title) {
      this.metaSrv.setTitle(title);
    }

    tags.forEach(t => {
      if (t.name) {
        this.updateTag(t.name, t.content);
      }
    });
  }

  private updateTag(tagName: string, contentTag: string) {
    this.metaSrv.removeTag(`property='${tagName}'`);
    this.metaSrv.removeTag(`name='${tagName}'`);
    if (
      contentTag !== null &&
      contentTag !== undefined &&
      `${contentTag}` !== ""
    ) {
      this.metaSrv.setTag(tagName, contentTag);
    }
  }

  /**
   * Single decision point for `<meta name="robots">`. Call it on every
   * NavigationEnd with the language-stripped router URL.
   *
   * Meta tags survive client-side navigation, so this must also REMOVE the tag
   * when the new route is indexable — otherwise the directive leaks from a tag
   * listing onto the REsource hub or an article page.
   */
  public applyRouteRobotsPolicy(url: string): void {
    const isNotIndexHost = this.isNoIndexHost();
    const isNotIndexRoute = this.isNoIndexRoute(url);
    if (isNotIndexHost || isNotIndexRoute) {
      this.updateTag("robots", "noindex, nofollow");
    } else {
      this.metaSrv.removeTag("name='robots'");
    }
  }

  private isNoIndexRoute(url: string): boolean {
    const path = (url || "").split(/[?#]/)[0].toLowerCase();
    return this.NOINDEX_URL_PREFIXES.some(
      prefix => path === prefix || path.startsWith(`${prefix}/`)
    );
  }

  private isNoIndexHost(): boolean {
    const host = (window.location.host || "").toLowerCase();
    return this.NOINDEX_HOSTS.indexOf(host) !== -1;
  }

  /**
   * Sets the `<meta name="prerender-status-code" content="<code>">` tag that
   * Prerender.io / search engines read to decide HTTP status.
   *
   * Clears the whole redirect pair first, not just the status tag: an earlier
   * in-app navigation may have emitted `301 + prerender-header: Location: ...`
   * for an off-canonical article URL, and that header survives a client-side
   * route change. Without the clear, a later 404/503 would be served to the
   * crawler alongside a stale `Location` pointing at an unrelated page.
   */
  setPrerenderStatusCode(code: number): void {
    clearPrerenderRedirect(document);
    this.metaSrv.setTag("prerender-status-code", String(code));
    this.prerenderReadyService.signalReady();
  }

  getMetaTagsFrom(
    title: string,
    description: string,
    keywords: string,
    imgPath?: string
  ): any[] {
    let baseImg = "/assets/icons/SOCIAL-SHARE-SiiLA.png";
    if (imgPath) {
      baseImg = imgPath;
    }

    const tags = [
      // general
      { name: "name", content: title },
      { name: "keywords", content: keywords },
      {
        name: "lang",
        content: this.siilaTranslationService.getCurrentLanguage()
      },
      {
        name: "og:url",
        content: this.encodeHrefOnce(this.getCanonicalHref())
      },

      { name: "og:site_name", content: this.SITE_BRAND },
      // Title
      { name: "og:title", content: title }, //don't prepend for og:title tag but do for generic and twitter
      { name: "twitter:title", content: title },
      {
        name: "mobile-web-app-title",
        content: encodeURIComponent(title)
      },
      // Description
      { name: "og:description", content: description },
      { name: "description", content: description },
      { name: "twitter:description", content: description },
      // Image
      {
        name: "og:image",
        property: "og:image",
        content: encodeURI(environment.applicationURL + baseImg)
      },
      {
        name: "image",
        content: encodeURI(environment.applicationURL + baseImg)
      },
      {
        name: "twitter:image",
        content: encodeURI(environment.applicationURL + baseImg)
      },
      {
        name: "og:image:width",
        content: 440
      },
      {
        name: "og:image:height",
        content: 220
      }
    ];

    //this.addCanonicalTag(`${encodeURI(window.location.href)}`);

    return tags;
  }

  /**
   * Collapse the trailing slash on the SITE ROOT only — `https://host/` becomes
   * `https://host`. Every URL that has a real path is returned untouched.
   *
   * The root is the one URL where this app's two URL builders disagreed. A
   * browser normalizes the root to `https://host/`, so `addCanonicalTag` — fed
   * `window.location.href` — emitted the slash; the hreflang set is built via
   * `SiilaTranslationService.applyLangSuffix`, which strips a trailing slash so
   * `/lang/<code>` attaches cleanly, so it emitted none. The homepage therefore
   * declared `https://host/` canonical while advertising `https://host` as its
   * own default-language and `x-default` alternate — the mismatch Ahrefs flagged.
   *
   * It also made the SAME page emit two different canonicals: arriving on
   * `/lang/<default>` left no slash behind once the suffix was stripped, while
   * arriving on `/` kept one.
   *
   * Deliberately anchored to `scheme://host` + only-slashes rather than a blanket
   * `/\/+$/`. A blanket strip would also rewrite `…/products/` and any other
   * trailing-slash URL a visitor happens to arrive on — URLs this app never
   * generates (the only trailing-slash link in the codebase is the root
   * `routerLink="/"`), so rewriting them would change canonical for pages the
   * ticket never covered, and would point them at a URL shape the server may not
   * serve. The anchoring also makes the helper inert for the relative paths
   * `UrlCanonicalService.apply` returns during SSR, and makes it impossible to
   * reduce a URL to the empty string (an empty `href` resolves to the current
   * page, which would silently make every URL self-canonical).
   */
  private stripRootTrailingSlash(url: string): string {
    return url.replace(/^(https?:\/\/[^/]+)\/+$/i, "$1");
  }

  public addCanonicalTag(currentPath: string) {
    let path = currentPath;
    if (path == null || path === undefined) {
      return path;
    }

    // remove request params from path
    path = path.split("?")[0];

    // Strip the trailing /lang/<code> segment ONLY when it is THIS deployment's
    // default language, so default-language variants collapse onto the clean URL
    // (killing "Duplicate without user-selected canonical") while real alternates
    // (e.g. /lang/en on BR) stay self-canonical and are linked via hreflang. The
    // default code is platform-aware (BR->pt-br, MX/CO->es, LATAM->en).
    const defaultCode = this.siilaTranslationService.getDefaultLangSuffixCode();
    path = path.replace(new RegExp(`/lang/${defaultCode}/?$`, "i"), "");

    // Root only: match the hreflang set, which is built through `applyLangSuffix`
    // and never carries a trailing slash. See `stripRootTrailingSlash`.
    path = this.stripRootTrailingSlash(path);

    var existingCanonicalLink = document.querySelector(
      `link[rel='canonical']`
    ) as HTMLLinkElement;

    if (!existingCanonicalLink) {
      const linkTag = document.createElement("link");
      linkTag.setAttribute("rel", "canonical");
      linkTag.href = path;
      document.head.appendChild(linkTag);
    } else {
      existingCanonicalLink.href = path;
    }
  }

  /**
   * Drop every `<link rel="alternate" hreflang="…">` currently in the head.
   *
   * Two callers: `addHreflangTags`, which clears before re-emitting, and the
   * 404 view. A page that fails to resolve must advertise NO alternates —
   * otherwise the set built for the REQUESTED URL survives the in-app route to
   * `/404` and tells crawlers that a URL answering 404 has translations.
   */
  public removeHreflangTags(): void {
    document
      .querySelectorAll(`link[rel='alternate'][hreflang]`)
      .forEach(el => el.parentNode && el.parentNode.removeChild(el));
  }

  /**
   * Meta contract for the 404 view: title, no hreflang, canonical on `/404`.
   *
   * `app.component` sets the canonical from `window.location.href` on every
   * `NavigationEnd`, and an entity that fails to resolve routes here IN-APP —
   * so without this the 404 page keeps advertising the REQUESTED URL as its own
   * canonical, and keeps that URL's hreflang set. Both are wrong on a page that
   * answers 404. Matches the behaviour SIIL-6111 shipped for news
   * (`siila.com.br/404/lang/en`): the canonical carries the ACTIVE language
   * suffix, which `applyLangSuffix` drops when it is the deployment default.
   */
  public setNotFoundTags(): void {
    const sts = this.siilaTranslationService;
    this.setDynamicTags(
      [],
      sts.getTranslationFromJSONFiles("metatags.notFound.tabTitle"),
      true
    );
    this.removeHreflangTags();
    this.addCanonicalTag(
      sts.buildCrossCountryUrl(
        environment.platformId,
        "404",
        sts.getCurrentLanguage()
      )
    );
  }

  /**
   * Emit `<link rel="alternate" hreflang="…">` tags for every language THIS
   * deployment supports, plus an `x-default` pointing at the default-language
   * (clean) URL. Derives the alternate set from the platform's supported
   * languages so it is correct on all four deployments (BR: pt-BR+en, MX/CO:
   * es+en, LATAM: en). All URLs are built through `buildCrossCountryUrl`, which
   * strips any incoming `/lang` suffix and re-applies the conditional one — so
   * passing the clean base OR a `/lang` variant yields the SAME set, satisfying
   * hreflang reciprocity on every URL variant. Pass the route path (not
   * `window.location.href`) to stay SSR-safe and avoid the replaceState mutation.
   */
  public addHreflangTags(
    basePath: string,
    localizePathForLang?: (lang: string) => string,
    originalRegionId?: number
  ) {
    if (basePath == null || basePath === undefined) {
      return;
    }

    // hreflang alternates must advertise the CLEAN page URL: query params
    // (tracking codes, Wistia's `?wtime={seek_to_second_number}` seek param,
    // etc.) are per-visit noise, and keeping them let crawlers discover an
    // ever-growing family of `?…/lang/en` variants of the same page.
    basePath = basePath.split("?")[0];

    try {
      // Remove any existing hreflang tags before re-emitting.
      this.removeHreflangTags();

      const sts = this.siilaTranslationService;
      const platform = environment.platformId;
      const defaultLang = sts.getDefaultLanguage();

      // Default resolver preserves the original behavior (same path for every
      // language). Routes whose slug changes per language (news/noticias) pass a
      // resolver that rewrites the path prefix + translated title per language.
      const resolve = localizePathForLang || (() => basePath);

      let tags = sts.getSupportedLanguages().map(lang => ({
        hreflang: this.HREFLANG_CODE[lang] || lang,
        href: sts.buildCrossCountryUrl(platform, resolve(lang), lang)
      }));

      // On LATAM, a news item originally published for Brazil (region 101) has no
      // Spanish version — drop the `es` alternate so we don't advertise a
      // non-existent translation to search engines.
      const isBrazilOnLatam =
        platform === "latam" &&
        originalRegionId != null &&
        originalRegionId === 101;
      if (isBrazilOnLatam) {
        tags = tags.filter(t => t.hreflang !== "es");
      }

      // Stash the per-language LOCAL (current-deployment) address-bar paths so the
      // language switcher can re-slug the URL INSTANTLY on switch — no page-data
      // reload. Only for pages whose slug is language-dependent (a resolver was
      // passed); same-path pages keep the plain suffix behavior. Honors the
      // latam BR-news es-drop so we never register a non-existent translation.
      if (localizePathForLang) {
        const localMap: { [lang: string]: string } = {};
        sts.getSupportedLanguages().forEach(lang => {
          if (isBrazilOnLatam && lang === "es") {
            return;
          }
          localMap[lang] = sts.buildLocalizedPath("/" + resolve(lang), lang);
        });
        sts.setLocalizedPathMap(localMap);
      }

      tags.push({
        hreflang: "x-default",
        href: sts.buildCrossCountryUrl(
          platform,
          resolve(defaultLang),
          defaultLang
        )
      });

      tags.forEach(t => {
        if (!t.href) {
          return;
        }
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", t.hreflang);
        link.setAttribute("href", this.encodeHrefOnce(t.href));
        document.head.appendChild(link);
      });
    } catch (e) {
      console.error("Error adding hreflang tags:", e);
    }
  }

  /**
   * `encodeURI` is NOT idempotent: it re-encodes `%` itself, so a URL that
   * already carries a percent-escape gains a `%25` layer on every pass
   * (`%7B` -> `%257B` -> `%25257B`, the ladder Search Console surfaced). Only
   * encode a URL that carries no escape yet; an already-encoded one is final.
   */
  private encodeHrefOnce(href: string): string {
    return /%[0-9A-Fa-f]{2}/.test(href) ? href : encodeURI(href);
  }

  private contactEmail() {
    return environment.platformId === "mx" || environment.platformId == "co"
      ? "contacto@siila.com.mx"
      : environment.platformId === "br"
      ? "contato@siila.com.br"
      : "contact@siila.com";
  }

  /**
   * Resolve a meta-tag value for THIS deployment's country.
   *
   * Order: `<baseKey>.<platformId>` -> `<baseKey>.default` -> `<baseKey>`.
   *
   * Shape-tolerant on purpose. ngx-translate's parser cannot descend into a
   * string leaf, so a key still stored as a plain string falls through to the
   * third step and resolves exactly as it does today. That is what lets this
   * ship without touching any page other than the homepage.
   *
   * A total miss returns the base key, which is what ngx-translate itself
   * returns for an unknown key — so a page with no key at all keeps rendering
   * what it renders today rather than going blank.
   *
   * The `typeof v === "string"` guard matters: reading a migrated (object) leaf
   * through the flat path returns the OBJECT, which would otherwise be stamped
   * into a meta tag as "[object Object]".
   */
  private resolveCountryMetaValue(baseKey: string): string {
    const hit = (key: string): string | null => {
      const value = this.siilaTranslationService.getTranslationFromJSONFiles(
        key
      );
      return typeof value === "string" && value !== "" && value !== key
        ? value
        : null;
    };

    return (
      hit(`${baseKey}.${environment.platformId}`) ??
      hit(`${baseKey}.default`) ??
      hit(baseKey) ??
      baseKey
    );
  }

  public setHomePageMetaTags() {
    const title = this.resolveCountryMetaValue("metatags.home.title");
    const tabTitle = this.resolveCountryMetaValue("metatags.home.tabTitle");
    const description = this.resolveCountryMetaValue("metatags.home.subject");
    const keywords = this.resolveCountryMetaValue("metatags.home.keywords");
    let tags = this.getMetaTagsFrom(title, description, keywords);
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setSpotPageMetaTags() {
    const title = this.resolveCountryMetaValue("metatags.spot.title");
    const tabTitle = this.resolveCountryMetaValue("metatags.spot.tabTitle");
    const content = this.resolveCountryMetaValue("metatags.spot.subject");
    const keywords = this.resolveCountryMetaValue("metatags.spot.keywords");
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setAllProductsMetaTags() {
    const title = this.resolveCountryMetaValue("metatags.allProducts.title");
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.allProducts.tabTitle"
    );
    const content = this.resolveCountryMetaValue(
      "metatags.allProducts.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.allProducts.keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setMarketAnalyticsMetaTags() {
    const title = this.resolveCountryMetaValue(
      "metatags.marketAnalytics.title"
    );
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.marketAnalytics.tabTitle"
    );
    const content = this.resolveCountryMetaValue(
      "metatags.marketAnalytics.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.marketAnalytics.keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setAcademyMetaTags() {
    const title = this.resolveCountryMetaValue("metatags.academy.title");
    const tabTitle = this.resolveCountryMetaValue("metatags.academy.tabTitle");
    const content = this.resolveCountryMetaValue("metatags.academy.subject");
    const keywords = this.resolveCountryMetaValue("metatags.academy.keywords");
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setFibraAnalyticsMetaTags() {
    const title = this.resolveCountryMetaValue("metatags.fibraanalytics.title");
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.fibraanalytics.tabTitle"
    );
    const content = this.resolveCountryMetaValue(
      "metatags.fibraanalytics.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.fibraanalytics.keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setFiiDataProMetaTags() {
    const title = this.resolveCountryMetaValue("metatags.fiiDataPro.title");
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.fiiDataPro.tabTitle"
    );
    const content = this.resolveCountryMetaValue("metatags.fiiDataPro.subject");
    const keywords = this.resolveCountryMetaValue(
      "metatags.fiiDataPro.keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setAboutMetaTags() {
    const rootFolder = "metatags.about.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setProductPageMetaTags(product: DetailedProduct) {
    if (product.id == 103 || product.id == 105 || product.id == 108) {
      this.setInvestorSurveyTags();
      return;
    }
    // Grocs BR
    if (product.id == 101 || product.id == 106) {
      this.setGrocsTags();
      return;
    }

    if (product.id == 102 || product.id == 104 || product.id === 107) {
      this.setIndicesTags();
      return;
    }

    const rootFolder = "metatags.solution.";
    const productName = this.siilaTranslationService.getTranslation(
      product.productShortName
    );
    const title = "SiiLA: " + productName;
    const tabTitle = productName;

    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setIndicesTags() {
    const title = this.resolveCountryMetaValue("metatags.indices.title");
    const tabTitle = this.resolveCountryMetaValue("metatags.indices.tabTitle");
    const content = this.resolveCountryMetaValue("metatags.indices.subject");
    const keywords = this.resolveCountryMetaValue("metatags.indices.keywords");

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setGrocsTags() {
    const title = this.resolveCountryMetaValue("metatags.grocs.title");
    const tabTitle = this.resolveCountryMetaValue("metatags.grocs.tabTitle");
    const content = this.resolveCountryMetaValue("metatags.grocs.subject");
    const keywords = this.resolveCountryMetaValue("metatags.grocs.keywords");

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setInvestorSurveyTags() {
    const title = this.resolveCountryMetaValue("metatags.investorSurvey.title");
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.investorSurvey.tabTitle"
    );
    const content = this.resolveCountryMetaValue(
      "metatags.investorSurvey.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.investorSurvey.keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  removeHTMLCode(htmlText: string) {
    var div = document.createElement("div");
    div.innerHTML = htmlText;
    var text = div.textContent || div.innerText || "";
    text = text ? text.replace(/[\n\r\v\f]/g, " ") : "";
    return text;
  }

  stripRichHtmlForMeta(htmlText: string): string {
    if (!htmlText) return "";
    const withSpacedBlocks = htmlText.replace(
      /<\s*\/?(p|div|li|ul|ol|h[1-6]|section|article|header|footer|tr|td|th|blockquote|figure|figcaption)\b[^>]*>|<\s*br\s*\/?\s*>/gi,
      " "
    );
    const div = document.createElement("div");
    div.innerHTML = withSpacedBlocks;
    const text = div.textContent || div.innerText || "";
    return text.replace(/\s+/g, " ").trim();
  }

  truncateText(text: string, maxLength: number = 250) {
    if (text.length <= maxLength) {
      return text;
    }
    const truncatedText = text.substring(0, maxLength - 3);
    const lastSpaceIndex = truncatedText.lastIndexOf(" ");
    if (lastSpaceIndex !== -1) {
      return truncatedText.substring(0, lastSpaceIndex) + "...";
    }
    return truncatedText;
  }

  getCloudImgURL(item: string): string {
    const url = item.replace("***", "1280w");
    return `${environment.cloudfrontURL}${url}`;
  }

  public setInsightsDetailTags(news: NewsDetail) {
    const tabTitle = `${this.siilaTranslationService.getTranslation(
      news.headline
    )}`;
    const title = tabTitle;
    let content: string;
    const summary = news.summary
      ? this.siilaTranslationService.getTranslation(news.summary)
      : null;
    if (summary) {
      content = this.stripRichHtmlForMeta(summary);
      content = this.truncateText(content, 160);
    } else {
      content = this.siilaTranslationService.getTranslation(
        news.primaryContent
      );
      if (content) {
        content = this.stripRichHtmlForMeta(content);
        content = this.truncateText(content, 160);
      }
    }

    let headerImgCaption;
    if (news.headerImageCaption) {
      headerImgCaption = this.siilaTranslationService.getTranslation(
        news.headerImageCaption
      );
    }
    const imageAlt =
      headerImgCaption ||
      this.siilaTranslationService.getTranslation(news.headline);

    const headerImg = this.getCloudImgURL(news.headerImage.fullPath);
    let tags = this.getMetaTagsFrom(title, content, null);
    if (tags) {
      for (let t of tags) {
        if (
          t.name === "og:image" ||
          t.name === "image" ||
          t.name === "twitter:image"
        ) {
          t.content = encodeURI(headerImg);
        }
        if (t.name === "og:image:width") {
          t.content = this.NEWS_IMAGE_WIDTH;
        }
        if (t.name === "og:image:height") {
          t.content = this.NEWS_IMAGE_HEIGHT;
        }
      }
      tags.push({ name: "og:image:alt", content: imageAlt });
      tags.push({ name: "twitter:image:alt", content: imageAlt });
    }
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setNewsArticleJsonLd(news: NewsDetail): void {
    if (!news || !news.id) {
      return;
    }
    this.removeNewsArticleJsonLd();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = this.NEWS_ARTICLE_JSONLD_ID;
    script.text = JSON.stringify(this.buildNewsArticleSchema(news));
    document.head.appendChild(script);
  }

  public removeNewsArticleJsonLd(): void {
    const existing = document.getElementById(this.NEWS_ARTICLE_JSONLD_ID);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }
  }

  private getPublishTimezoneOffset(news: NewsDetail): string {
    return news.regionOriginId === 101 ? "-03:00" : "-06:00";
  }

  private toIsoDateWithTimezone(dateStr: string, offset: string): string {
    return dateStr ? `${dateStr}T00:00:00${offset}` : dateStr;
  }

  private buildNewsArticleSchema(news: NewsDetail): { [key: string]: unknown } {
    const sts = this.siilaTranslationService;
    const domainRoot = environment.applicationURL;
    const lang = sts.getCurrentLanguage();

    const publishDate = this.toIsoDateWithTimezone(
      news.publishDate,
      this.getPublishTimezoneOffset(news)
    );
    const description = this.stripRichHtmlForMeta(
      news.summary ? sts.getTranslation(news.summary) : ""
    );
    const imageUrl = this.getLargestNewsImageUrl(news);

    const schema: { [key: string]: unknown } = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: sts.getTranslation(news.headline),
      datePublished: publishDate,
      dateModified: publishDate,
      publisher: {
        "@type": "Organization",
        name: this.SITE_BRAND,
        url: domainRoot,
        logo: {
          "@type": "ImageObject",
          url: `${domainRoot}/assets/icons/SOCIAL-SHARE-SiiLA.png`
        }
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": this.getCanonicalHref()
      },
      inLanguage: this.HREFLANG_CODE[lang] || lang
    };

    if (description) {
      schema.description = description;
    }
    if (imageUrl) {
      schema.image = {
        "@type": "ImageObject",
        url: imageUrl,
        width: this.NEWS_IMAGE_WIDTH,
        height: this.NEWS_IMAGE_HEIGHT
      };
    }

    const authors = this.buildNewsArticleAuthors(news, domainRoot);
    schema.author =
      authors.length > 0
        ? authors
        : [{ "@type": "Organization", name: this.SITE_BRAND, url: domainRoot }];

    return schema;
  }

  private buildNewsArticleAuthors(
    news: NewsDetail,
    domainRoot: string
  ): NewsArticleAuthor[] {
    const authors: NewsArticleAuthor[] = [];

    (news.internalAuthors || []).forEach(rawName => {
      const name = (rawName || "").trim();
      if (!name) {
        return;
      }
      if (name === "SiiLA News") {
        authors.push({ "@type": "Organization", name, url: domainRoot });
      } else {
        authors.push({ "@type": "Person", name });
      }
    });

    (news.externalAuthors || []).forEach(ext => {
      if (!ext || !ext.name) {
        return;
      }
      const name = this.stripRichHtmlForMeta(
        this.siilaTranslationService.getTranslation(ext.name)
      ).trim();
      if (!name) {
        return;
      }
      authors.push({ "@type": "Person", name });
    });

    return authors;
  }

  private getLargestNewsImageUrl(news: NewsDetail): string {
    const fullPath =
      news.headerImage && news.headerImage.fullPath
        ? news.headerImage.fullPath
        : "";
    return fullPath ? this.getCloudImgURL(fullPath) : "";
  }

  private getCanonicalHref(): string {
    const link = document.querySelector(
      "link[rel='canonical']"
    ) as HTMLLinkElement;
    const href = link ? link.getAttribute("href") : null;
    return href || window.location.href.split("?")[0];
  }

  public setNewsArticleOgTags(news: NewsDetail): void {
    if (!news || !news.id) {
      return;
    }
    this.clearArticleMetaTags();
    this.setOgTypeMeta("article");

    if (news.publishDate) {
      const isoPublishDate = this.toIsoDateWithTimezone(
        news.publishDate,
        this.getPublishTimezoneOffset(news)
      );
      this.appendArticleMeta("article:published_time", isoPublishDate);
      this.appendArticleMeta("article:modified_time", isoPublishDate);
    }

    this.buildNewsArticleAuthors(
      news,
      environment.applicationURL
    ).forEach(author => this.appendArticleMeta("article:author", author.name));

    const section =
      news.topic && news.topic.name
        ? this.siilaTranslationService.getTranslation(news.topic.name)
        : "";
    if (section) {
      this.appendArticleMeta("article:section", section);
    }
  }

  public removeNewsArticleOgTags(): void {
    this.clearArticleMetaTags();
    this.setOgTypeMeta("website");
  }

  private clearArticleMetaTags(): void {
    document
      .querySelectorAll("meta[property^='article:']")
      .forEach(el => el.parentNode && el.parentNode.removeChild(el));
  }

  private appendArticleMeta(property: string, content: string): void {
    const meta = document.createElement("meta");
    meta.setAttribute("property", property);
    meta.setAttribute("content", content);
    document.head.appendChild(meta);
  }

  private setOgTypeMeta(value: string): void {
    let tag = document.querySelector(
      "meta[property='og:type']"
    ) as HTMLMetaElement;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", "og:type");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", value);
  }

  public setSolutionstMetaTags(solution: DetailedSolution) {
    if (solution.id == 101 || solution.id == 108) {
      this.setInstitutionalInvestors();
      return;
    }

    if (solution.id == 102 || solution.id == 109) {
      this.setBrokerageTags();
      return;
    }

    if (solution.id == 103 || solution.id == 110) {
      this.setOwnersOperatorDeveloperTags();
      return;
    }

    if (solution.id == 104 || solution.id == 111) {
      this.setTenantsTags();
      return;
    }

    if (solution.id == 105 || solution.id == 112) {
      this.setValuationAdvisorTags();
      return;
    }

    if (solution.id == 106 || solution.id == 113) {
      this.setServiceProviderTags();
      return;
    }

    if (solution.id == 107 || solution.id == 114 || solution.id == 122) {
      this.setReitInvestorTags();
      return;
    }

    const solutionName = this.siilaTranslationService.getTranslation(
      solution.shortName
    );
    const title = "SiiLA: " + solutionName;
    const tabTitle = solutionName;

    const content = this.resolveCountryMetaValue("metatags.solution.subject");
    const keywords = this.resolveCountryMetaValue("metatags.solution.keywords");
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setInstitutionalInvestors() {
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.investors.tabTitle"
    );
    const title = this.resolveCountryMetaValue("metatags.investors.title");
    const content = this.resolveCountryMetaValue("metatags.investors.subject");
    const keywords = this.resolveCountryMetaValue(
      "metatags.investors.keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setOwnersOperatorDeveloperTags() {
    const title = this.resolveCountryMetaValue(
      "metatags.owneroperatordevelopers.title"
    );
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.owneroperatordevelopers.tabTitle"
    );
    const content = this.resolveCountryMetaValue(
      "metatags.owneroperatordevelopers.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.owneroperatordevelopers.keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setTenantsTags() {
    const tabTitle = this.resolveCountryMetaValue("metatags.tenants.tabTitle");
    const title = this.resolveCountryMetaValue("metatags.tenants.title");
    const content = this.resolveCountryMetaValue("metatags.tenants.subject");
    const keywords = this.resolveCountryMetaValue("metatags.tenants.keywords");

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setInsightsHomeTags() {
    const platformId = environment.platformId;
    const rootFolder = "metatags.insights.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject" + platformId
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(
      title,
      content,
      keywords,
      this.RESOURCE_SOCIAL_SHARE_IMG
    );
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setPressHomeTags() {
    const platformId = environment.platformId;
    const rootFolder = "metatags.press.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setNxtPodcastMetaTags() {
    const rootFolder = "metatags.nxtPodcast.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(
      title,
      content,
      keywords,
      "/assets/img/nxt-podcast/hero-nxt-podcast-1600.jpg"
    );
    this.setDynamicTags(tags, tabTitle, true);
    this.setPodcastSeriesJsonLd(content);
  }

  /**
   * Injects the PodcastSeries structured data as a <script type="application/
   * ld+json"> in <head>. No existing service owns JSON-LD injection (only
   * canonical/hreflang <link> tags have that pattern today via
   * `addCanonicalTag`/`addHreflangTags`), so this mirrors the same
   * find-or-create-then-set approach for a <script> tag instead of a <link>.
   */
  private setPodcastSeriesJsonLd(description: string): void {
    const scriptId = "nxt-podcast-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "PodcastSeries",
      name: "NXT Podcast",
      url: `${environment.applicationURL}/nxt-podcast`,
      description,
      inLanguage: this.siilaTranslationService.getCurrentLanguage(),
      author: { "@type": "Person", name: "Giancarlo Nicastro" },
      publisher: {
        "@type": "Organization",
        name: "SiiLA",
        url: environment.applicationURL
      },
      webFeed:
        "https://www.youtube.com/playlist?list=PLnEjq-A0vrMGwsByAyTn-fpEIJH13tWpM",
      sameAs: [
        "https://www.youtube.com/playlist?list=PLnEjq-A0vrMGwsByAyTn-fpEIJH13tWpM",
        "https://open.spotify.com/show/41Cd3FdI244CGxwAGqlnZ7",
        "https://music.amazon.com.br/podcasts/490a6c09-c4ca-461b-8d3e-a260cf1fa787/nxt-podcast",
        "https://www.deezer.com/br/show/1001629411"
      ]
    });
    document.head.appendChild(script);
  }

  setBrokerageTags() {
    const title = this.resolveCountryMetaValue("metatags.brokerage.title");
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.brokerage.tabTitle"
    );
    const content = this.resolveCountryMetaValue("metatags.brokerage.subject");
    const keywords = this.resolveCountryMetaValue(
      "metatags.brokerage.keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setValuationAdvisorTags() {
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.valuationAdvisor.tabTitle"
    );
    const title = this.resolveCountryMetaValue(
      "metatags.valuationAdvisor.title"
    );
    const content = this.resolveCountryMetaValue(
      "metatags.valuationAdvisor.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.valuationAdvisor.keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setServiceProviderTags() {
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.serviceProvider.tabTitle"
    );
    const title = this.resolveCountryMetaValue(
      "metatags.serviceProvider.title"
    );
    const content = this.resolveCountryMetaValue(
      "metatags.serviceProvider.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.serviceProvider.keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setReitInvestorTags() {
    const tabTitle = this.resolveCountryMetaValue(
      "metatags.reitInvestors.tabTitle"
    );
    const title = this.resolveCountryMetaValue("metatags.reitInvestors.title");
    const content = this.resolveCountryMetaValue(
      "metatags.reitInvestors.subject"
    );
    const keywords = this.resolveCountryMetaValue(
      "metatags.reitInvestors.keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setSolutionTags(rootFolder: string) {
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }
}
